// DE scorecard (screen 4): the period's headline tiles, the scorecard table with an 8-week
// trend per metric, and "what moved the score" (contributors). One API call:
// `GET /digital-employees/{id}/scorecard?period=` (the seat scorecard's alias), the same
// numbers acceptance 2 compares with `GET /seats/{id}/scorecard`. TEAM-KPI (founder-approved
// 2026-10-21): the attainment cell carries the line's health under the metric's own bands.
import {
  Badge,
  BandBadge,
  EmptyState,
  LensBandDot,
  SectionHeading,
  Sparkline,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { Api } from "../../api/client";
import type { Scorecard, ScorecardMetric } from "../../api/types";
import { weekRangeLabel } from "../../focus/weeks";
import { AgentReportLine, AgentReportMark } from "../agent-report";
import {
  formatAttainment,
  formatHours,
  formatMetricValue,
  formatMoney,
  formatScore,
  formatTarget,
  STATE_LABELS,
  TRACKING_LABELS,
} from "../format";
import { HEALTH_LABEL, HEALTH_TEXT, healthTone, lineHealth } from "../kpi/health";
import { deScorecardQuery } from "../queries";
import {
  KpiReportingLink,
  ReportingStatusLine,
  reportingProps,
} from "../reporting/reporting-status";
import { coverageText, Loading, ProvisionalBadge } from "../screen-parts";
import { ErrorNotice } from "../structure/parts";

/** A titled block of the tab: `section aria-labelledby` with the template's heading. */
function Block({
  id,
  title,
  description,
  actions,
  children,
}: {
  id: string;
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="mt-8 flex min-w-0 flex-col">
      <SectionHeading headingId={id} title={title} description={description} actions={actions} />
      {children}
    </section>
  );
}

export function ScorecardTab({
  api,
  companyId,
  id,
  week,
}: {
  api: Api;
  companyId: string;
  id: string;
  week: string;
}) {
  const card = useQuery(deScorecardQuery(api, companyId, id, week));
  if (card.isPending) return <Loading label="Loading the scorecard" rows={6} />;
  if (card.isError) return <ErrorNotice what="The scorecard" error={card.error} />;
  return <ScorecardView card={card.data} />;
}

export function ScorecardView({ card }: { card: Scorecard }) {
  const currency = card.cost?.currency ?? "USD";
  return (
    <div className="flex min-w-0 flex-col" data-slot="de-scorecard">
      <StatGrid className="mt-0 mb-0">
        <StatTile
          label="KPI score"
          value={formatScore(card.kpi_score)}
          band={card.band}
          detail={coverageText(card.completeness, card.band)}
        />
        <StatTile label="Goal score" value={formatScore(card.goal_score)} />
        <StatTile
          label="Cost (week)"
          value={card.cost ? formatMoney(card.cost.total_minor, currency) : "—"}
        />
        <StatTile
          label="Supervisor hours"
          value={card.hours ? formatHours(card.hours.supervision_minutes) : "—"}
          detail={
            card.hours?.founder_minutes !== null && card.hours?.founder_minutes !== undefined
              ? `Founder ${formatHours(card.hours.founder_minutes)} (agency only)`
              : undefined
          }
        />
      </StatGrid>
      <Block
        id="scorecard-table-heading"
        title="Scorecard"
        description={`Week ${card.period.key} (${weekRangeLabel(card.period.key)})`}
        actions={
          <>
            <BandBadge band={card.band} />
            <ProvisionalBadge completeness={card.completeness} band={card.band} />
            {card.subject.kind === "seat" ? <KpiReportingLink seatId={card.subject.id} /> : null}
          </>
        }
      >
        {card.metrics.length ? (
          <MetricsTable metrics={card.metrics} currency={currency} period={card.period.key} />
        ) : (
          <EmptyState
            variant="card"
            title="No metrics on this seat"
            description="A manager adds metrics and weights under Setup › Seats."
          />
        )}
      </Block>
      <Block
        id="contributors-heading"
        title="What moved the score"
        description="KPIs short of target, ranked by drag: goal weight × shortfall in score points."
      >
        {card.contributors.length ? (
          <Table>
            <TableCaption>Contributors, biggest drag first. Only shortfalls drag.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">KPI</TableHead>
                <TableHead scope="col" className="text-right">
                  Weight
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Shortfall
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Drag
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {card.contributors.map((c) => (
                <TableRow key={c.id} data-contributor={c.id}>
                  <TableHead scope="row" className="font-normal">
                    {c.name}
                  </TableHead>
                  {/* domain-kpi Contributor: weight as configured (not normalised), shortfall
                      in score points below 100, impact = weight × shortfall (a drag, ≥ 0). */}
                  <TableCell className="text-right tabular-nums" data-field="weight">
                    {formatNumber(c.weight)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-field="shortfall">
                    {formatNumber(c.shortfall)} pts
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-field="impact">
                    {formatNumber(c.impact)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            variant="card"
            title="Nothing moved the score yet"
            description="Contributors appear once KPIs have data."
          />
        )}
      </Block>
    </div>
  );
}

/** The chart's text alternative: every week with its actual and its target (both series). */
export function trendLabel(m: ScorecardMetric, currency: string, period: string): string {
  const weeks = m.trend.map((p) => {
    const actual = p.actual === null ? "no data" : formatMetricValue(p.actual, m.unit, currency);
    // A trend point carries no upper bound: only the scorecard's own week may use
    // `target_max` (the grid's rule); an earlier range target is not described.
    const target =
      p.target === null
        ? "no target"
        : m.direction === "between" && p.period_key !== period
          ? "range target not recorded"
          : `target ${formatTarget({ ...m, target: p.target }, currency)}`;
    return `${p.period_key} ${actual} (${target})`;
  });
  return `${m.name}, last ${m.trend.length} weeks: ${weeks.join("; ")}`;
}

function formatNumber(v: number): string {
  return (Math.round(v * 10) / 10).toLocaleString("en-US");
}

function MetricsTable({
  metrics,
  currency,
  period,
}: {
  metrics: ScorecardMetric[];
  currency: string;
  period: string;
}) {
  return (
    <Table>
      <TableCaption>Actual against target per metric, with the 8-week trend.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Metric</TableHead>
          <TableHead scope="col" className="text-right">
            Actual
          </TableHead>
          <TableHead scope="col" className="text-right">
            Target
          </TableHead>
          <TableHead scope="col" className="text-right">
            Attainment
          </TableHead>
          <TableHead scope="col">State</TableHead>
          <TableHead scope="col">Trend</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {metrics.map((m) => (
          <TableRow key={m.metric_def_id} data-metric={m.key}>
            <TableHead scope="row" className="font-normal">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium">{m.name}</span>
                <Badge variant="neutral">{TRACKING_LABELS[m.tracking_mode]}</Badge>
                {m.visibility === "agency" ? <Badge variant="accent">Agency</Badge> : null}
                <AgentReportMark metric={m} />
              </span>
              <AgentReportLine metric={m} />
              <ReportingStatusLine
                compact
                reporting={reportingProps(m).reporting}
                pendingReview={reportingProps(m).pendingReview}
              />
            </TableHead>
            <TableCell className="text-right tabular-nums" data-field="actual">
              {formatMetricValue(m.actual, m.unit, currency)}
            </TableCell>
            <TableCell className="text-right tabular-nums" data-field="target">
              {formatTarget(m, currency)}
            </TableCell>
            <TableCell
              className={`text-right tabular-nums ${HEALTH_TEXT[lineHealth(m) ?? "none"]}`}
              data-field="attainment"
              data-health={lineHealth(m) ?? "none"}
              title={HEALTH_LABEL[lineHealth(m) ?? "none"]}
            >
              <span className="inline-flex items-center justify-end gap-1.5">
                <LensBandDot band={healthTone(lineHealth(m))} />
                {formatAttainment(m.attainment)}
              </span>
            </TableCell>
            <TableCell className="text-muted-foreground">{STATE_LABELS[m.state]}</TableCell>
            <TableCell>
              <Sparkline
                label={trendLabel(m, currency, period)}
                values={m.trend.map((p) => p.actual)}
                reference={m.trend.map((p) => p.target)}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
